/* eslint-disable jsx-a11y/aria-role */
/*
 * Exception for ARIA role="text" being added to axe-core in 4.2, this will currently display as an error in an automated test using either Axe or Lighthouse
 * https://github.com/dequelabs/axe-core/milestone/19
 * https://github.com/dequelabs/axe-core/issues/1597
 */
import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import { BLOCKS } from '@contentful/rich-text-types';

import RichText from '../../../Core/RichText';
import Link from '../../../Core/Link';
import { StyledSticky } from '../styles';
import { BannerContext } from '../../../Providers/Banner';
import TabCarousel from '../TabCarousel';
import { ResponsiveContext } from '../../../Providers/Responsive';

import {
  Wrapper,
  Tab,
  ContentWrapper,
  ImageWrapper,
  Heading,
  CardImage,
  Body,
  Carousel,
} from './styles';

const TabList = ({
  componentStyle = '',
  columnCount = '4',
  isSticky = false,
  data = null,
  className = '',
  activeColumnIdx = null,
  firstSelectableCell = null,
  setActiveColumn = null,
}) => {
  // * Null check for data
  if (!data) return false;

  const intl = useIntl();

  const [tabs, setTabs] = useState(null);

  const body = {
    renderNode: {
      /*
       * Exception for ARIA role="text" being added to axe-core in 4.2, this will currently display as an error in an automated test using either Axe or Lighthouse
       * https://github.com/dequelabs/axe-core/milestone/19
       * https://github.com/dequelabs/axe-core/issues/1597
       */
      // eslint-disable-next-line jsx-a11y/aria-role
      [BLOCKS.PARAGRAPH]: (_, children) => <p role="text">{children}</p>,
    },
  };

  const { mobile } = useContext(ResponsiveContext);

  // tabs to be used for mobile
  const selectableTabs = data.filter(dataItem => {
    return dataItem.selectable;
  });

  const unselectableTabs = data.filter(dataItem => {
    return !dataItem.selectable;
  });

  const {
    state: { notificationBannerState },
  } = useContext(BannerContext);

  // For easy reference
  const keys = {
    end: 35,
    home: 36,
    left: 37,
    up: 38,
    right: 39,
    down: 40,
    enter: 13,
    space: 32,
  };

  // Add or subtract depending on key pressed
  const direction = {
    37: -1,
    38: -1,
    39: 1,
    40: 1,
  };

  // Handle keydown on tabs
  const keydownEventListener = event => {
    const key = event.keyCode;

    switch (key) {
      case keys.end:
        event.preventDefault();
        // Activate last tab
        focusLastTab();
        break;

      case keys.home:
        event.preventDefault();
        // Activate first tab
        focusFirstTab();
        break;

      // Up and down are in keydown because we need to prevent page scroll
      case keys.up:
      case keys.down:
        determineOrientation(event);
        break;

      default:
        break;
    }
  };

  // Handle keyup on tabs
  const keyupEventListener = event => {
    const key = event.keyCode;

    const indexedTab = parseInt(event.target.dataset.index, 10);

    switch (key) {
      case keys.left:
      case keys.right:
        determineOrientation(event);
        break;

      case keys.enter:
      case keys.space:
        setActiveColumn(indexedTab);
        break;

      default:
        break;
    }
  };

  // When a tablist's aria-orientation is set to vertical,
  // only up and down arrow should function.
  // In all other cases only left and right arrow function.
  const determineOrientation = event => {
    const key = event.keyCode;

    let proceed = false;

    if (key === keys.up || key === keys.down) {
      event.preventDefault();
      proceed = false;
    } else if (key === keys.left || key === keys.right) {
      proceed = true;
    }

    if (proceed) {
      switchTabOnArrowPress(event);
    }
  };

  // Either focus the next, previous, first, or last tab
  // depending on key pressed
  const switchTabOnArrowPress = event => {
    const pressed = event.keyCode;

    if (direction[pressed]) {
      const { target } = event;
      const indexedTab = parseInt(target.dataset.index, 10);

      if (indexedTab !== undefined) {
        if (
          tabs[indexedTab + direction[pressed]] &&
          indexedTab + direction[[pressed]] >= 2
        ) {
          tabs[indexedTab + direction[pressed]].focus();
        } else if (pressed === keys.left) {
          focusLastTab();
        } else if (pressed === keys.right) {
          focusFirstTab();
        }
      }
    }
  };

  // Make a guess
  const focusFirstTab = () => {
    tabs[2].focus();
  };

  // Make a guess
  const focusLastTab = () => {
    tabs[tabs.length - 1].focus();
  };

  useEffect(() => {
    setTabs(document.querySelectorAll('[role="tab"]'));
  }, []);

  return (
    <Wrapper
      className={className}
      columnCount={columnCount}
      role="tablist"
      aria-label="PC Financial Mastercard comparison chart"
    >
      {mobile ? (
        <>
          {unselectableTabs.map(dataItem => {
            // * Destructure dataItem values
            const {
              id,
              selectable,
              heading,
              bodyCopy,
              imageDam,
              link,
              backgroundStyle,
            } = dataItem;

            return (
              <Tab
                key={dataItem.id}
                id={
                  imageDam
                    ? imageDam.altText || ''.toLowerCase().replace(/\s/g, '-')
                    : id
                }
                className={`${className} 
                  ${componentStyle} 
                  ${backgroundStyle} 
                  mobile`}
                as="div"
                columnCount={columnCount}
                selectable={selectable}
              >
                {isSticky === true ? (
                  <StyledSticky
                    boundaryElement=".chartArea"
                    topOffset={70}
                    // Track whether the notification banner status is fixed or not, to justify for padding
                    stickyStyle={{
                      paddingTop:
                        notificationBannerState === 'fixed' ? '70px' : '0',
                      zIndex: '100',
                    }}
                    lang={intl.locale}
                    stickyClassName={
                      mobile ? 'sticky mobile-landscape' : 'sticky'
                    }
                  >
                    <ContentWrapper
                      className={`${className} ${backgroundStyle}`}
                      columnCount={columnCount}
                    >
                      {link ? (
                        <>
                          <Link className="unstyled-link" id={id} to={link}>
                            {imageDam && (
                              <ImageWrapper>
                                <CardImage src={imageDam} alt="" />
                              </ImageWrapper>
                            )}
                            {heading && (
                              <Heading lang={intl.locale} role="text">
                                <RichText document={heading} />
                              </Heading>
                            )}
                          </Link>
                          {bodyCopy && (
                            <Body>
                              <RichText document={bodyCopy} options={body} />
                            </Body>
                          )}
                        </>
                      ) : (
                        <>
                          {imageDam && (
                            <ImageWrapper>
                              <CardImage src={imageDam} alt="" />
                            </ImageWrapper>
                          )}
                          {heading && (
                            <Heading role="text">
                              <RichText document={heading} />
                            </Heading>
                          )}
                          {bodyCopy && (
                            <Body>
                              <RichText document={bodyCopy} options={body} />
                            </Body>
                          )}
                        </>
                      )}
                    </ContentWrapper>
                  </StyledSticky>
                ) : (
                  <ContentWrapper
                    className={`${className} ${backgroundStyle}`}
                    columnCount={columnCount}
                  >
                    {link ? (
                      <>
                        <Link className="unstyled-link" id={id} to={link}>
                          {imageDam && (
                            <ImageWrapper>
                              <CardImage src={imageDam} alt="" />
                            </ImageWrapper>
                          )}
                          {heading && (
                            <Heading lang={intl.locale} role="text">
                              <RichText document={heading} />
                            </Heading>
                          )}
                        </Link>
                        {bodyCopy && (
                          <Body>
                            <RichText document={bodyCopy} options={body} />
                          </Body>
                        )}
                      </>
                    ) : (
                      <>
                        {imageDam && (
                          <ImageWrapper>
                            <CardImage src={imageDam} alt="" />
                          </ImageWrapper>
                        )}
                        {heading && (
                          <Heading role="text">
                            <RichText document={heading} />
                          </Heading>
                        )}
                        {bodyCopy && (
                          <Body>
                            <RichText document={bodyCopy} options={body} />
                          </Body>
                        )}
                      </>
                    )}
                  </ContentWrapper>
                )}
              </Tab>
            );
          })}
          <Carousel className={`${className} ${componentStyle}`}>
            {isSticky === true ? (
              <StyledSticky
                boundaryElement=".chartArea"
                topOffset={70}
                // Track whether the notification banner status is fixed or not, to justify for padding
                stickyStyle={{
                  paddingTop:
                    notificationBannerState === 'fixed' ? '70px' : '0',
                  zIndex: '100',
                }}
                lang={intl.locale}
                stickyClassName={mobile ? 'sticky mobile-landscape' : 'sticky'}
              >
                <TabCarousel
                  data={data}
                  selectableTabs={selectableTabs}
                  className={className}
                  columnCount={columnCount}
                  setActiveColumn={setActiveColumn}
                  componentStyle={componentStyle}
                />
              </StyledSticky>
            ) : (
              <TabCarousel
                data={selectableTabs}
                className={className}
                columnCount={columnCount}
                setActiveColumn={setActiveColumn}
                componentStyle={componentStyle}
              />
            )}
          </Carousel>
        </>
      ) : (
        <>
          {data.map((dataItem, index) => {
            // * Destructure dataItem values
            const {
              id,
              selectable,
              heading,
              bodyCopy,
              imageDam,
              link,
              backgroundStyle,
            } = dataItem;

            let isNotActive = '';

            if (className.includes('selectable-card chart')) {
              isNotActive =
                (selectable && !activeColumnIdx) ||
                index < firstSelectableCell ||
                (activeColumnIdx >= firstSelectableCell &&
                  activeColumnIdx === index)
                  ? 'is-active'
                  : 'is-not-active';
            }

            let isSelectable = '';

            if (selectable === true) {
              isSelectable = selectable ? 'is-selectable' : '';
            }

            return (
              <Tab
                key={dataItem.id}
                id={`tab-${id}`}
                data-index={index}
                className={`
                  ${className} 
                  ${componentStyle} 
                  ${backgroundStyle} 
                  ${isNotActive} 
                  ${isSelectable}`}
                as={selectable ? 'button' : 'div'}
                columnCount={columnCount}
                onClick={
                  className.includes('selectable-card chart') && selectable
                    ? () => setActiveColumn(index)
                    : null
                }
                onKeyDown={keydownEventListener}
                onKeyUp={keyupEventListener}
                isNotActive={isNotActive}
                selectable={selectable}
                role="tab"
                aria-controls={selectable ? 'chart-tab' : null}
                aria-selected={selectable && !isNotActive ? 'true' : 'false'}
                tabIndex={selectable && !isNotActive ? null : '-1'}
              >
                {isSticky === true ? (
                  <StyledSticky
                    boundaryElement=".chartArea"
                    dontUpdateHolderHeightWhenSticky="true"
                    topOffset={70}
                    // Track whether the notification banner status is fixed or not, to justify for padding
                    stickyStyle={{
                      paddingTop:
                        notificationBannerState === 'fixed' ? '70px' : '0',
                      zIndex: '100',
                    }}
                    lang={intl.locale}
                    stickyClassName={
                      mobile ? 'sticky mobile-landscape' : 'sticky'
                    }
                  >
                    <ContentWrapper
                      className={`${className} ${backgroundStyle}`}
                      columnCount={columnCount}
                    >
                      {link ? (
                        <>
                          <Link className="unstyled-link" id={id} to={link}>
                            {imageDam && (
                              <ImageWrapper>
                                <CardImage src={imageDam} alt="" />
                              </ImageWrapper>
                            )}
                            {heading && (
                              <Heading lang={intl.locale} role="text">
                                <RichText document={heading} />
                              </Heading>
                            )}
                          </Link>
                          {bodyCopy && (
                            <Body>
                              <RichText document={bodyCopy} options={body} />
                            </Body>
                          )}
                        </>
                      ) : (
                        <>
                          {imageDam && (
                            <ImageWrapper>
                              <CardImage src={imageDam} alt="" />
                            </ImageWrapper>
                          )}
                          {heading && (
                            <Heading role="text">
                              <RichText document={heading} />
                            </Heading>
                          )}
                          {bodyCopy && (
                            <Body>
                              <RichText document={bodyCopy} options={body} />
                            </Body>
                          )}
                        </>
                      )}
                    </ContentWrapper>
                  </StyledSticky>
                ) : (
                  <ContentWrapper
                    className={`${className} ${backgroundStyle}`}
                    columnCount={columnCount}
                  >
                    {link ? (
                      <>
                        <Link className="unstyled-link" id={id} to={link}>
                          {imageDam && (
                            <ImageWrapper>
                              <CardImage src={imageDam} alt="" />
                            </ImageWrapper>
                          )}
                          {heading && (
                            <Heading lang={intl.locale} role="text">
                              <RichText document={heading} />
                            </Heading>
                          )}
                        </Link>
                        {bodyCopy && (
                          <Body>
                            <RichText document={bodyCopy} options={body} />
                          </Body>
                        )}
                      </>
                    ) : (
                      <>
                        {imageDam && (
                          <ImageWrapper>
                            <CardImage src={imageDam} alt="" />
                          </ImageWrapper>
                        )}
                        {heading && (
                          <Heading role="text">
                            <RichText document={heading} />
                          </Heading>
                        )}
                        {bodyCopy && (
                          <Body>
                            <RichText document={bodyCopy} options={body} />
                          </Body>
                        )}
                      </>
                    )}
                  </ContentWrapper>
                )}
              </Tab>
            );
          })}
        </>
      )}
    </Wrapper>
  );
};

TabList.propTypes = {
  columnCount: PropTypes.string,
  isSticky: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.shape()),
  componentStyle: PropTypes.string,
  className: PropTypes.string,
  activeColumnIdx: PropTypes.number,
  firstSelectableCell: PropTypes.number,
  setActiveColumn: PropTypes.func,
};

export default TabList;
